import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type { ApprovePaymentsInput, ApprovePaymentsResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * Hook for approving multiple payments at once.
 * Supports two modes:
 * 1. Pay run: Approve all payments for a specific pay run date (with optional exclusions)
 * 2. Payment IDs: Approve specific payments by their IDs
 */
export function useApprovePayments() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<ApprovePaymentsResponse, Error, ApprovePaymentsInput>({
    mutationFn: async (input) => {
      return await rpcClient.finance.payments.approveMultiple(input);
    },
    onSuccess: (data) => {
      const { approvedCount, approvedPaymentIds } = data;

      showToast({
        title: 'Success',
        message: `Approved ${approvedCount} payment${approvedCount !== 1 ? 's' : ''}`,
        color: 'success',
        icon: 'check',
      });

      // Invalidate payments list to refresh the data
      void queryClient.invalidateQueries({
        queryKey: orpc.finance.payments.list.queryKey({
          input: {},
        }),
      });

      // Invalidate individual payment queries for the approved payments
      approvedPaymentIds.forEach((paymentId) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.finance.payments.get.queryKey({
            input: { id: paymentId },
          }),
        });
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to approve payments',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
